import { z } from "zod";

import type { Domain } from "@/domain";
import {
  boundedTextSchema,
  hasUniqueValues,
  kebabCaseIdentifierSchema,
  localOggAudioReferenceSchema,
  localResourceReferenceSchema,
  nonNegativeSafeIntegerSchema
} from "@/domain/schemas";
import { singleChoicePayloadSchema } from "@/game/templates/choice-match";
import { dragOrganizePayloadSchema } from "@/game/templates/drag-organize";
import { instructionPayloadSchema } from "@/game/templates/instruction";
import { rhythmPayloadSchema } from "@/game/templates/rhythm";
import { scenarioPayloadSchema } from "@/game/templates/scenario";
import { targetSearchPayloadSchema } from "@/game/templates/search";
import { sequencePayloadSchema } from "@/game/templates/sequence";

export type ActivityPayloadSchema = z.ZodType;

export type ActivityPayloadSchemaCatalog = Readonly<
  Record<Domain, Readonly<Record<string, ActivityPayloadSchema>>>
>;

function copyIssues(
  result: z.ZodSafeParseResult<unknown>,
  context: z.RefinementCtx,
  mapPath: (path: PropertyKey[]) => PropertyKey[] = (path) => path
): void {
  if (result.success) {
    return;
  }

  for (const issue of result.error.issues) {
    context.addIssue({
      code: "custom",
      path: mapPath(issue.path),
      message: issue.message
    });
  }
}

const choiceItemSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  label: boundedTextSchema(120)
});

const soundBubblesPayloadSchema = z
  .strictObject({
    spokenText: boundedTextSchema(120),
    soundAudio: localResourceReferenceSchema,
    question: boundedTextSchema(300),
    choices: z.array(choiceItemSchema).min(2).max(12),
    correctChoiceId: kebabCaseIdentifierSchema,
    hint: boundedTextSchema(300)
  })
  .superRefine((payload, context) => {
    copyIssues(
      singleChoicePayloadSchema.safeParse({
        kind: "single-choice",
        choices: payload.choices,
        correctChoiceId: payload.correctChoiceId
      }),
      context
    );
  });

const pictureItemSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  asset: localResourceReferenceSchema,
  alt: boundedTextSchema(160)
});

const pictureCharacterMatchPayloadSchema = z
  .strictObject({
    pictureItems: z.array(pictureItemSchema).min(2).max(12),
    characterItems: z.array(choiceItemSchema).min(2).max(12),
    matches: z
      .array(
        z.strictObject({
          pictureId: kebabCaseIdentifierSchema,
          characterId: kebabCaseIdentifierSchema
        })
      )
      .min(2)
      .max(12),
    hint: boundedTextSchema(300)
  })
  .superRefine((payload, context) => {
    const pictureIds = payload.pictureItems.map(({ id }) => id);
    const characterIds = payload.characterItems.map(({ id }) => id);
    const matchedPictureIds = payload.matches.map(({ pictureId }) => pictureId);
    const matchedCharacterIds = payload.matches.map(
      ({ characterId }) => characterId
    );

    if (!hasUniqueValues(pictureIds)) {
      context.addIssue({
        code: "custom",
        path: ["pictureItems"],
        message: "Picture identifiers must be unique"
      });
    }

    if (!hasUniqueValues(characterIds)) {
      context.addIssue({
        code: "custom",
        path: ["characterItems"],
        message: "Character identifiers must be unique"
      });
    }

    if (
      payload.pictureItems.length !== payload.characterItems.length ||
      payload.matches.length !== payload.pictureItems.length ||
      !hasUniqueValues(matchedPictureIds) ||
      !hasUniqueValues(matchedCharacterIds)
    ) {
      context.addIssue({
        code: "custom",
        path: ["matches"],
        message: "Matches must pair every picture and character exactly once"
      });
    }

    const knownPictureIds = new Set(pictureIds);
    const knownCharacterIds = new Set(characterIds);

    payload.matches.forEach((match, index) => {
      if (!knownPictureIds.has(match.pictureId)) {
        context.addIssue({
          code: "custom",
          path: ["matches", index, "pictureId"],
          message: "Match must reference a listed picture"
        });
      }

      if (!knownCharacterIds.has(match.characterId)) {
        context.addIssue({
          code: "custom",
          path: ["matches", index, "characterId"],
          message: "Match must reference a listed character"
        });
      }
    });
  });

const storySequencePayloadSchema = z
  .strictObject({
    narration: boundedTextSchema(500),
    narrationAudio: localResourceReferenceSchema,
    items: z.array(choiceItemSchema).min(2),
    correctOrder: z.array(kebabCaseIdentifierSchema).min(2),
    hint: boundedTextSchema(300)
  })
  .superRefine((payload, context) => {
    copyIssues(
      sequencePayloadSchema.safeParse({
        items: payload.items,
        correctOrder: payload.correctOrder
      }),
      context
    );
  });

const quantityChoiceSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  count: nonNegativeSafeIntegerSchema.pipe(z.number().max(20))
});

const quantityChoicesSchema = z
  .array(quantityChoiceSchema)
  .min(2)
  .max(12)
  .refine((choices) => hasUniqueValues(choices.map(({ id }) => id)), {
    message: "Choice identifiers must be unique"
  })
  .refine((choices) => hasUniqueValues(choices.map(({ count }) => count)), {
    message: "Choice quantities must be unique"
  });

function validateQuantityChoice(
  choices: readonly z.infer<typeof quantityChoiceSchema>[],
  correctChoiceId: string,
  expectedCount: number,
  context: z.RefinementCtx
): void {
  copyIssues(
    singleChoicePayloadSchema.safeParse({
      kind: "single-choice",
      choices: choices.map((choice) => ({
        id: choice.id,
        label: String(choice.count)
      })),
      correctChoiceId
    }),
    context
  );

  const correctChoice = choices.find(({ id }) => id === correctChoiceId);
  if (correctChoice !== undefined && correctChoice.count !== expectedCount) {
    context.addIssue({
      code: "custom",
      path: ["correctChoiceId"],
      message: "Correct choice does not match the expected quantity"
    });
  }
}

const numberTrainMatchSchema = z
  .strictObject({
    mode: z.literal("match"),
    targetCount: nonNegativeSafeIntegerSchema.pipe(z.number().max(20)),
    choices: quantityChoicesSchema,
    correctChoiceId: kebabCaseIdentifierSchema
  })
  .superRefine((payload, context) => {
    validateQuantityChoice(
      payload.choices,
      payload.correctChoiceId,
      payload.targetCount,
      context
    );
  });

const numberTrainCompareSchema = z
  .strictObject({
    mode: z.literal("compare"),
    relation: z.enum(["most", "fewest"]),
    choices: quantityChoicesSchema,
    correctChoiceId: kebabCaseIdentifierSchema
  })
  .superRefine((payload, context) => {
    const counts = payload.choices.map(({ count }) => count);
    const expectedCount =
      payload.relation === "most" ? Math.max(...counts) : Math.min(...counts);
    validateQuantityChoice(
      payload.choices,
      payload.correctChoiceId,
      expectedCount,
      context
    );
  });

const numberTrainPayloadSchema = z.union([
  numberTrainMatchSchema,
  numberTrainCompareSchema
]);

const forestMarketPayloadSchema = z
  .strictObject({
    itemId: kebabCaseIdentifierSchema,
    operation: z.enum(["add", "subtract"]),
    startingCount: nonNegativeSafeIntegerSchema.pipe(z.number().max(20)),
    changeCount: nonNegativeSafeIntegerSchema.pipe(z.number().max(20)),
    choices: quantityChoicesSchema,
    correctChoiceId: kebabCaseIdentifierSchema
  })
  .superRefine((payload, context) => {
    const expectedCount =
      payload.operation === "add"
        ? payload.startingCount + payload.changeCount
        : payload.startingCount - payload.changeCount;

    if (expectedCount < 0) {
      context.addIssue({
        code: "custom",
        path: ["changeCount"],
        message: "Subtraction cannot produce a negative quantity"
      });
      return;
    }

    validateQuantityChoice(
      payload.choices,
      payload.correctChoiceId,
      expectedCount,
      context
    );
  });

const patternTokenSchema = z.enum([
  "acorn",
  "flower",
  "leaf",
  "mushroom",
  "shell",
  "star"
]);

const patternChoiceSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  token: patternTokenSchema
});

const patternChoicesSchema = z
  .array(patternChoiceSchema)
  .min(2)
  .max(8)
  .refine((choices) => hasUniqueValues(choices.map(({ id }) => id)), {
    message: "Choice identifiers must be unique"
  })
  .refine((choices) => hasUniqueValues(choices.map(({ token }) => token)), {
    message: "Choice tokens must be unique"
  });

const sequencePatternPayloadSchema = z
  .strictObject({
    mode: z.literal("sequence"),
    repeatUnit: z.array(patternTokenSchema).min(2).max(6),
    sequence: z.array(patternTokenSchema.nullable()).min(3).max(24),
    choices: patternChoicesSchema,
    correctChoiceId: kebabCaseIdentifierSchema
  })
  .superRefine((payload, context) => {
    const emptyIndexes = payload.sequence.flatMap((token, index) =>
      token === null ? [index] : []
    );

    if (emptyIndexes.length !== 1) {
      context.addIssue({
        code: "custom",
        path: ["sequence"],
        message: "Sequence must contain exactly one empty position"
      });
      return;
    }

    const emptyIndex = emptyIndexes[0];
    if (emptyIndex === undefined) {
      return;
    }

    payload.sequence.forEach((token, index) => {
      const expectedToken = payload.repeatUnit[index % payload.repeatUnit.length];
      if (token !== null && token !== expectedToken) {
        context.addIssue({
          code: "custom",
          path: ["sequence", index],
          message: "Sequence token does not follow the repeat unit"
        });
      }
    });

    const correctChoice = payload.choices.find(
      ({ id }) => id === payload.correctChoiceId
    );
    const expectedToken = payload.repeatUnit[emptyIndex % payload.repeatUnit.length];

    if (correctChoice === undefined || correctChoice.token !== expectedToken) {
      context.addIssue({
        code: "custom",
        path: ["correctChoiceId"],
        message: "Correct choice must fill the empty sequence position"
      });
    }
  });

const gridPositionSchema = z.strictObject({
  column: nonNegativeSafeIntegerSchema,
  row: nonNegativeSafeIntegerSchema
});

const routeChoiceSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  position: gridPositionSchema
});

const spatialRoutePayloadSchema = z
  .strictObject({
    mode: z.literal("route"),
    columns: z.number().int().min(2).max(10),
    rows: z.number().int().min(2).max(10),
    start: gridPositionSchema,
    moves: z.array(z.enum(["up", "right", "down", "left"])).min(1).max(12),
    choices: z.array(routeChoiceSchema).min(2).max(8),
    correctChoiceId: kebabCaseIdentifierSchema
  })
  .superRefine((payload, context) => {
    if (!hasUniqueValues(payload.choices.map(({ id }) => id))) {
      context.addIssue({
        code: "custom",
        path: ["choices"],
        message: "Choice identifiers must be unique"
      });
    }

    const isInBounds = (position: z.infer<typeof gridPositionSchema>) =>
      position.column < payload.columns && position.row < payload.rows;

    if (!isInBounds(payload.start)) {
      context.addIssue({
        code: "custom",
        path: ["start"],
        message: "Start position must be inside the grid"
      });
      return;
    }

    payload.choices.forEach((choice, index) => {
      if (!isInBounds(choice.position)) {
        context.addIssue({
          code: "custom",
          path: ["choices", index, "position"],
          message: "Choice position must be inside the grid"
        });
      }
    });

    const endpoint = { ...payload.start };
    payload.moves.forEach((move, index) => {
      if (move === "up") endpoint.row -= 1;
      if (move === "right") endpoint.column += 1;
      if (move === "down") endpoint.row += 1;
      if (move === "left") endpoint.column -= 1;

      if (
        endpoint.column < 0 ||
        endpoint.column >= payload.columns ||
        endpoint.row < 0 ||
        endpoint.row >= payload.rows
      ) {
        context.addIssue({
          code: "custom",
          path: ["moves", index],
          message: "Route leaves the grid"
        });
      }
    });

    const correctChoice = payload.choices.find(
      ({ id }) => id === payload.correctChoiceId
    );
    if (
      correctChoice === undefined ||
      correctChoice.position.column !== endpoint.column ||
      correctChoice.position.row !== endpoint.row
    ) {
      context.addIssue({
        code: "custom",
        path: ["correctChoiceId"],
        message: "Correct choice must match the route endpoint"
      });
    }
  });

const patternBridgePayloadSchema = z.union([
  sequencePatternPayloadSchema,
  spatialRoutePayloadSchema
]);

const englishTokenSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-z]+(?:-[a-z]+)*$/u, "Expected a lowercase English token");

const englishChoicesSchema = z
  .array(englishTokenSchema)
  .min(2)
  .max(12)
  .refine((tokens) => hasUniqueValues(tokens), {
    message: "Choice tokens must be unique"
  });

const englishTokenAudioSchema = z.record(
  englishTokenSchema,
  localOggAudioReferenceSchema
);

function validateTokenAudioKeys(
  tokens: readonly string[],
  tokenAudio: Readonly<Record<string, string>>,
  context: z.RefinementCtx
): void {
  const expectedKeys = new Set(tokens);
  const actualKeys = Object.keys(tokenAudio);

  if (
    actualKeys.length !== expectedKeys.size ||
    actualKeys.some((token) => !expectedKeys.has(token))
  ) {
    context.addIssue({
      code: "custom",
      path: ["tokenAudio"],
      message: "Token audio keys must exactly match choice tokens"
    });
  }
}

function validateEnglishChoice(
  choices: readonly string[],
  answer: string,
  context: z.RefinementCtx
): void {
  copyIssues(
    singleChoicePayloadSchema.safeParse({
      kind: "single-choice",
      choices: choices.map((token) => ({ id: token, label: token })),
      correctChoiceId: answer
    }),
    context,
    (path) => {
      if (path[0] === "choices") {
        return ["choiceTokens", ...path.slice(1)];
      }
      if (path[0] === "correctChoiceId") {
        return ["answerTokens", 0];
      }
      return path;
    }
  );
}

const letterSoundPayloadSchema = z
  .strictObject({
    kind: z.literal("letter-sound"),
    soundAudio: localOggAudioReferenceSchema,
    exampleWord: englishTokenSchema,
    choiceTokens: englishChoicesSchema,
    answerTokens: z.tuple([englishTokenSchema])
  })
  .superRefine((payload, context) => {
    validateEnglishChoice(
      payload.choiceTokens,
      payload.answerTokens[0],
      context
    );
  });

const pictureWordMatchPayloadSchema = z
  .strictObject({
    kind: z.literal("picture-word-match"),
    pictureAsset: localResourceReferenceSchema,
    wordAudio: localOggAudioReferenceSchema,
    tokenAudio: englishTokenAudioSchema,
    choiceTokens: englishChoicesSchema,
    answerTokens: z.tuple([englishTokenSchema])
  })
  .superRefine((payload, context) => {
    validateEnglishChoice(
      payload.choiceTokens,
      payload.answerTokens[0],
      context
    );
    validateTokenAudioKeys(
      payload.choiceTokens,
      payload.tokenAudio,
      context
    );
  });

export const ACTIVITY_PAYLOAD_SCHEMAS = {
  chinese: {
    "sound-bubbles": soundBubblesPayloadSchema,
    "picture-character-match": pictureCharacterMatchPayloadSchema,
    "story-sequence": storySequencePayloadSchema
  },
  math: {
    "number-train": numberTrainPayloadSchema,
    "forest-market": forestMarketPayloadSchema,
    "pattern-bridge": patternBridgePayloadSchema
  },
  english: {
    "letter-sound": letterSoundPayloadSchema,
    "picture-word-match": pictureWordMatchPayloadSchema,
    "rhythm-phrases": rhythmPayloadSchema
  },
  habits: {
    "pack-schoolbag": dragOrganizePayloadSchema,
    "daily-routine": sequencePayloadSchema,
    "school-scenarios": scenarioPayloadSchema
  },
  "focus-social": {
    "target-search": targetSearchPayloadSchema,
    "follow-directions": instructionPayloadSchema,
    "friendship-stories": scenarioPayloadSchema
  }
} as const satisfies ActivityPayloadSchemaCatalog;
